import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import type { ContentPlaceholderSlice } from 'graphql-ts-types';
import { space } from 'styled-system';

import { CssGrid } from 'common/components/CssGrid';
import RichTextBlock from 'common/components/RichText';
import { HeadlineH7 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import OfferTeaserItem from './OfferTeaserItem';

interface OfferTeaserProps extends Partial<
  Pick<ContentPlaceholderSlice, 'titleText' | 'isTitleCenterAligned'>
> {
  items: Array<Omit<ComponentProps<typeof OfferTeaserItem>, 'isEnhanced' | 'index'>>;
}

const LAST_TEASER_INDEX = 2;

const teaserGridStyles: ComponentProps<typeof CssGrid> = {
  gridGap: ['sp_16', 'sp_24', null, 'sp_32'],
  gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', null, 'repeat(3, 1fr)'],
};

const Title = styled(RichTextBlock)<SpaceProps>`
  ${space};
`;

const titleStyles: ComponentProps<typeof Title> = {
  mb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
};

const OfferTeaser = ({ items, titleText, isTitleCenterAligned }: OfferTeaserProps) => {
  const { t } = useLocalization();
  const headingId = useId();

  return (
    <section
      data-corgi-id="offer-teaser"
      {...(titleText
        ? { 'aria-labelledby': headingId }
        : { 'aria-label': t('h24_current_promotions') })}
    >
      {titleText && (
        <Title {...titleStyles} {...(isTitleCenterAligned ? { textAlign: 'center' } : {})}>
          <HeadlineH7 id={headingId} as="span" dangerouslySetInnerHTML={{ __html: titleText }} />
        </Title>
      )}
      <CssGrid {...teaserGridStyles}>
        {items.map((item, index) => (
          <OfferTeaserItem
            {...item}
            isEnhanced={index === LAST_TEASER_INDEX}
            index={index}
            key={`offer-teaser-item-${index}`}
          />
        ))}
      </CssGrid>
    </section>
  );
};

export default OfferTeaser;
